import { useId, useState } from 'react';
import {
  Button, Checkbox, FormField, Input, Modal, ModalActions, ModalFootNote, PickPreview, Segmented,
} from '../../../components/shared';
import { useMenuEnabledFn } from '../../../lib/menu-gate';
import {
  useDashboardStore, type BoardKind, type BoardVisibility, type WidgetSpan,
} from '../../../store/dashboardStore';
import { WIDGET_DEFS, WIDGET_CAT_LABEL, getWidgetDef, type WidgetCat } from '../board/widget-registry';
import { WidgetChromeContext, DEFAULT_CHROME } from '../board/widget-chrome';
import { WidgetActionsContext, type WidgetActions } from '../board/widget-context';
import {
  STARTER_TEMPLATES, droppedNote, pickStarterWidgets, type CreateBoardPreset, type StarterTemplate,
} from '../board/starter-templates';
import { widgetMenuBlocked } from '../board/widget-menu-keys';
import { KIND_OPTIONS, VISIBILITY_OPTIONS } from '../board/BoardSettingsV2Modal';
import '../board/dashboard-frame-v2.css';
import './dashboard-create-v2.css';

/**
 * 새 대시보드 창 v2 (2026-09-16 · 대시보드 시안 v2 V6) — 깃발이 켜졌을 때만. 꺼져 있으면 `DashboardCreateModal` 그대로.
 *
 * 왼쪽: 이름 · 유형 · 공개 범위 · 시작 틀 넷 · 분류별 위젯 체크(쓸 폭 · 메뉴 꺼짐).
 * 오른쪽: 탭 줄 + 고른 위젯이 **폭대로 깔린** 새 대시보드(실제 위젯 · 누를 수 없음).
 *
 * ★★★ 시작 틀은 **이름을 채워 준다** — 다만 사람이 적은 이름은 건드리지 않는다(틀이 넣어 준 이름일 때만 바꾼다).
 * ★★ 이름이 비면 「만들기」가 꺼지고 **왜 꺼졌는지** 발에 적는다. 한 번 비워 두고 떠나면 칸 아래에 오류로도 적는다.
 * ★★ 공개 범위 · 전사 유형은 설정 창 v2 와 **같은 선택지**다(D11 · 부서 · 멤버 공유는 준비 중).
 * ★ 메뉴가 꺼진 위젯은 줄은 보이되 담을 수 없다 — 틀에서도 빠진다(D12).
 * ★ 미리보기 위젯은 대시보드와 같은 캐시(60초)를 나눠 쓴다. 행동은 빈 함수, 누르기는 `PickPreview` 가 `inert` 로 막는다.
 */

type TemplateChoice = 'blank' | StarterTemplate['id'];
interface Picked { key: string; span: WidgetSpan }

const CATS: WidgetCat[] = ['work', 'plan', 'talk', 'info'];
const SPAN_LABEL: Record<WidgetSpan, string> = { 4: '1/3', 6: '1/2', 8: '2/3', 12: '전체' };
/**
 * 권한이 필요한 «설정»이 있는 위젯에 다는 꼬리표 (시안 `axi-dashboard-v2` 의 같은 자리).
 *
 * ★★★ 시안은 여기에 「팀장 이상」이라고 적었지만 **그대로 쓰지 않는다 — 사실과 다르다.**
 *     위젯 자체는 **누구나 담을 수 있고**, 담으면 「내 팀」으로 보인다. 권한이 필요한 것은
 *     그 위젯의 **«범위» 설정**이다(팀 근무 현황: 부서=부서장 · 전사=근태 담당자·관리자).
 *     「팀장 이상」이라고 적으면 담을 수 있는 사람이 «나는 못 담는다»로 읽는다 —
 *     **틀린 안내는 없는 안내보다 나쁘다**(2026-09-16 관제 결정 (나)).
 * ⚠ 등록부에 권한 칸을 새로 만들지는 않았다 — 같은 표시가 여러 위젯에 필요해지면 그때 만든다(관제 결정, (다) 보류).
 */
const SCOPE_NOTE: Record<string, string> = { 'team-presence': '범위 일부는 권한 필요' };
const INERT: WidgetActions = { go: () => {}, openAttendanceRequest: () => {}, openSettings: () => {} };

export function DashboardCreateV2Modal({ onClose, preset }: { onClose: () => void; preset?: CreateBoardPreset }) {
  const createBoard = useDashboardStore((s) => s.createBoard);
  const setWidgetSpan = useDashboardStore((s) => s.setWidgetSpan);
  const boards = useDashboardStore((s) => s.boards);
  const hiddenBoardIds = useDashboardStore((s) => s.hiddenBoardIds);
  const menuEnabled = useMenuEnabledFn();
  const uid = useId();

  const [name, setName] = useState(preset?.name ?? '');
  const [kind, setKind] = useState<BoardKind>('personal');
  const [visibility, setVisibility] = useState<BoardVisibility>('private');
  const [template, setTemplate] = useState<TemplateChoice | null>(preset?.templateId ?? null);
  const [picked, setPicked] = useState<Picked[]>(preset?.widgets ?? []);
  const [note, setNote] = useState<string | null>(preset?.note ?? null);
  const [nameTouched, setNameTouched] = useState(false);

  const empty = name.trim() === '';
  const templateName = (id: TemplateChoice | null) => STARTER_TEMPLATES.find((t) => t.id === id)?.name;

  const chooseTemplate = (id: TemplateChoice) => {
    // 틀이 넣어 준 이름(또는 빈 이름)일 때만 이름을 따라 바꾼다 — 사람이 적은 이름은 그대로.
    const followName = empty || name === templateName(template);
    const t = STARTER_TEMPLATES.find((x) => x.id === id);
    if (t) {
      const pick = pickStarterWidgets(t, menuEnabled);
      setPicked(pick.widgets.map(({ key, span }) => ({ key, span })));
      setNote(droppedNote(pick.dropped));
      if (followName) setName(t.name);
    } else {
      setPicked([]);
      setNote(null);
      if (followName) setName('');
    }
    setTemplate(id);
  };

  const toggle = (key: string) => setPicked((cur) => {
    if (cur.some((p) => p.key === key)) return cur.filter((p) => p.key !== key);
    const def = getWidgetDef(key);
    return def ? [...cur, { key, span: def.defaultSpan }] : cur;
  });

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) { setNameTouched(true); return; }
    const id = createBoard({ name: trimmed, kind, visibility, widgetKeys: picked.map((p) => p.key) });
    const created = useDashboardStore.getState().boards.find((b) => b.id === id);
    created?.widgets.forEach((w, i) => {
      const span = picked[i]?.span;
      if (span && span !== w.span) setWidgetSpan(id, w.id, span);
    });
    onClose();
  };

  const form = (
    <div className="dwn-form">
      <FormField
        label="대시보드 이름" required
        error={nameTouched && empty ? '대시보드 이름을 적어 주세요' : undefined}
        hint={!nameTouched && empty ? '이름을 적어야 만들 수 있습니다' : undefined}
      >
        <Input
          value={name} maxLength={40} fullWidth autoFocus placeholder="예) 2026 프로젝트 관리"
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setNameTouched(true)}
        />
      </FormField>

      <FormField label="유형">
        <Segmented size="sm" ariaLabel="유형" value={kind} options={KIND_OPTIONS} onChange={(v) => setKind(v as BoardKind)} />
      </FormField>

      <FormField label="공개 범위">
        <Segmented
          size="sm" ariaLabel="공개 범위" value={visibility} options={VISIBILITY_OPTIONS}
          onChange={(v) => setVisibility(v as BoardVisibility)}
          hint="부서 공유 · 지정 멤버 공유는 준비 중입니다"
        />
      </FormField>

      <FormField label="시작 틀">
        <div className="dwn-tpls" role="radiogroup" aria-label="시작 틀">
          <label className="dwn-tpl">
            <input
              type="radio" className="dwn-sr" name="dwn-template"
              checked={template === 'blank'} onChange={() => chooseTemplate('blank')}
            />
            <span className="dwv-mini" aria-hidden><i className="dwv-mini__c dwv-mini__c--12" /></span>
            <b>빈 대시보드</b>
            <small>위젯 없이 시작</small>
          </label>
          {STARTER_TEMPLATES.map((t) => {
            const pick = pickStarterWidgets(t, menuEnabled);
            const tNote = droppedNote(pick.dropped);
            return (
              <label key={t.id} className="dwn-tpl">
                <input
                  type="radio" className="dwn-sr" name="dwn-template"
                  checked={template === t.id} disabled={pick.widgets.length === 0}
                  onChange={() => chooseTemplate(t.id)}
                />
                <span className="dwv-mini" aria-hidden>
                  {pick.widgets.map((w) => <i key={w.key} className={`dwv-mini__c dwv-mini__c--${w.span}`} />)}
                </span>
                <b>{t.name}</b>
                <small>{pick.widgets.map((w) => getWidgetDef(w.key)?.label ?? w.key).join(' · ') || '담을 위젯이 없습니다'}</small>
                {tNote && <small className="dwn-tpl__note">{tNote}</small>}
              </label>
            );
          })}
        </div>
      </FormField>

      <FormField label={`위젯 ${picked.length}개 담김`} hint="만든 뒤에도 더하고 뺄 수 있습니다">
        <div className="dwn-wlist" role="group" aria-label="담을 위젯">
          {CATS.map((cat) => {
            const items = WIDGET_DEFS.filter((d) => (d.cat ?? 'info') === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat} className="dwn-wgroup">
                <div className="dwn-gcap">{WIDGET_CAT_LABEL[cat]}</div>
                {items.map((d) => {
                  const blocked = widgetMenuBlocked(d.key, menuEnabled);
                  const mine = picked.find((p) => p.key === d.key);
                  return (
                    <div key={d.key} className={`dwn-wr${blocked ? ' is-off' : ''}`}>
                      {/* ⚠ id 를 줄마다 준다 — 안 주면 `FormField` 의 칸 id 하나를 모든 체크가 나눠 가져 라벨이 첫 줄만 가리킨다. */}
                      <Checkbox
                        id={`${uid}-w-${d.key}`} label={d.label} checked={Boolean(mine)} disabled={blocked}
                        onChange={() => toggle(d.key)}
                      />
                      {!blocked && SCOPE_NOTE[d.key] && <em className="dwn-wnote">{SCOPE_NOTE[d.key]}</em>}
                      <em>{blocked ? '메뉴 꺼짐' : SPAN_LABEL[mine?.span ?? d.defaultSpan]}</em>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </FormField>
    </div>
  );

  const openTabs = boards.filter((b) => !hiddenBoardIds.includes(b.id)).slice(0, 2);
  const preview = picked.length === 0
    ? (
      <div className="dwn-pvempty">
        <b>아직 고른 위젯이 없습니다</b>
        <span>시작 틀을 고르거나 왼쪽 목록에서 위젯을 담으면 여기에 새 대시보드가 깔립니다</span>
      </div>
    )
    : (
      <div className="dwn-pv">
        <div className="dwn-pvtabs">
          {openTabs.map((b) => <span key={b.id}>{b.name}</span>)}
          <span className={`is-on${empty ? ' is-noname' : ''}`}>{empty ? '이름 없음' : name.trim()}</span>
          <span>+</span>
        </div>
        <WidgetActionsContext.Provider value={INERT}>
          <WidgetChromeContext.Provider value={DEFAULT_CHROME}>
            <div className="dw-grid dwn-grid">
              {picked.map((p) => {
                const def = getWidgetDef(p.key);
                if (!def) return null;
                return (
                  <div key={p.key} className={`dw-slot dw-slot--${p.span}`}>
                    <def.Component />
                  </div>
                );
              })}
            </div>
          </WidgetChromeContext.Provider>
        </WidgetActionsContext.Provider>
      </div>
    );

  return (
    <Modal
      title="새 대시보드" accent size="xl" bodyFlat onClose={onClose}
      footer={(
        <>
          <ModalFootNote>
            위젯 <b>{picked.length}</b>개 담김
            {note ? ` · ${note}` : ''}
            {empty && <em className="dwn-foot-err"> · 이름을 적어 주세요</em>}
          </ModalFootNote>
          <ModalActions>
            <Button variant="secondary" onClick={onClose}>취소</Button>
            <Button variant="primary" onClick={submit} disabled={empty}>만들기</Button>
          </ModalActions>
        </>
      )}
    >
      <PickPreview
        className="dwn"
        preview={preview}
        previewTitle="미리보기"
        previewNote="고른 위젯이 폭대로 깔린 새 대시보드 · 누를 수 없음"
      >
        {form}
      </PickPreview>
    </Modal>
  );
}
